import type { PropsWithChildren } from "react";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import useClientContext from "../../../../../context/useClientContext";
import useMetaDataInitialization from "../hooks/useMetaDataInitialization";
import useReportMode from "../hooks/useReportMode";
import { useAppDispatch } from "../store/hooks/useAppDispatch";
import {
  selectClientSettingsLoaded,
  selectClientSettingsLoadingFailed,
} from "../store/selectors/clientSettingsSelectors";
import { selectMetaDataLoaded, selectMetaDataLoadingFailed } from "../store/selectors/metaDataSelectors";
import { loadClientSettings } from "../store/thunks/clientSettingsThunks";
import { fieldsStateActions } from "../store/thunks/fieldStatesThunks";
import { createCurrentReportAction, createMetaDataAction } from "../store/utils/storeUtils";
import ImportantDataLoadingFailed from "./common/ImportantDataLoadingFailed";
import ReportLoader from "./common/ReportLoader";

export const AppInitializer = ({ children }: PropsWithChildren) => {
  const { clientCode } = useClientContext();
  const metaDataLoaded = useSelector(selectMetaDataLoaded);
  const metaDataLoadingFailed = useSelector(selectMetaDataLoadingFailed);
  const dispatch = useAppDispatch();
  const settingsLoadingFailed = useSelector(selectClientSettingsLoadingFailed);
  const settingsLoaded = useSelector(selectClientSettingsLoaded);
  const { mode } = useReportMode();

  useEffect(() => {
    if (!clientCode) {
      return;
    }
    dispatch(loadClientSettings());
  }, [clientCode, dispatch]);

  const shouldLoadMetaData = (mode === "edit" || mode === "template") && !metaDataLoaded && clientCode !== undefined;
  useMetaDataInitialization(shouldLoadMetaData);

  useEffect(() => {
    return () => {
      dispatch(fieldsStateActions.resetFieldsState());
      dispatch(createMetaDataAction("reset"));
      dispatch(createCurrentReportAction("reset"));
    };
  }, [dispatch]);

  if (settingsLoadingFailed || metaDataLoadingFailed) {
    return (
      <ImportantDataLoadingFailed
        title="Failed to load the report"
        disclaimer="There appears to have been an issue with loading the data. Kindly attempt to reload the report. Should the issue persist, please get in touch with our support team for further assistance."
      />
    );
  }

  if ((!metaDataLoaded || !settingsLoaded) && (mode === "edit" || mode === "template")) {
    return <ReportLoader text="Loading metadata, it won't take long..." />;
  }

  return children;
};
